import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { categories, articleContents } from "@/mocks/categories";

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://superbuy.sbs';

const articleKeywordsMap: Record<string, string> = {
  "/superbuy-safety-review": "superbuy safety review, is superbuy safe, superbuy legit, superbuy security, superbuy payment protection",
  "/superbuy-vs-agents": "superbuy vs pandabuy, superbuy vs cssbuy, superbuy vs hoobuy, best buying agent 2026, agent comparison",
  "/best-superbuy-finds-2026": "best superbuy finds 2026, superbuy hidden gems, superbuy curated finds, superbuy spreadsheet finds",
  "/how-to-order-superbuy": "how to order superbuy, superbuy order tutorial, superbuy step by step, superbuy buying process",
  "/superbuy-shipping-guide": "superbuy shipping guide, superbuy shipping cost, superbuy shipping lines, international shipping guide",
  "/superbuy-qc-guide": "superbuy QC guide, quality control inspection, superbuy QC photos, how to inspect products",
  "/superbuy-payment-guide": "superbuy payment methods, superbuy payment security, superbuy paypal, safe payment superbuy",
  "/superbuy-streetwear-finds": "superbuy streetwear finds, streetwear spreadsheet, best streetwear superbuy, streetwear fashion 2026",
  "/superbuy-sneaker-spreadsheet": "superbuy sneaker spreadsheet, best sneaker finds, sneaker collector guide, rare sneakers superbuy",
  "/superbuy-hidden-gems": "superbuy hidden gems, underrated superbuy finds, secret superbuy categories, niche superbuy products",
};

function buildArticleJsonLd(slug: string, article: typeof articleContents[string]) {
  const fullUrl = `${siteUrl}${slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.subtitle,
      "author": { "@type": "Organization", "name": "Superbuy Spreadsheet" },
      "publisher": { "@type": "Organization", "name": "Superbuy Spreadsheet", "url": siteUrl },
      "datePublished": "2026-06-01",
      "dateModified": "2026-06-21",
      "mainEntityOfPage": { "@type": "WebPage", "@id": fullUrl },
      "image": article.image
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
        { "@type": "ListItem", "position": 2, "name": article.title, "item": fullUrl }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": article.faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": { "@type": "Answer", "text": faq.a }
      }))
    }
  ];
}

export default function GenericArticlePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const slug = location.pathname;

  const article = articleContents[slug];

  if (!article) {
    return (
      <>
        <SEOHead
          title="Article Not Found - Superbuy Spreadsheet"
          description="The article you are looking for could not be found. Browse our homepage for the latest superbuy spreadsheet guides and resources."
          canonicalPath={slug}
          noIndex={true}
        />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <button onClick={() => navigate("/")} className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Back to Homepage
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const seoKeywords = articleKeywordsMap[slug] || "superbuy spreadsheet, superbuy guide, superbuy finds";
  const jsonLd = buildArticleJsonLd(slug, article);

  const renderSectionContent = (section: typeof article.sections[0]) => {
    if (section.type === "table" && section.rows) {
      return (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-background-200">
                {section.rows[0].map((header: string, hi: number) => (
                  <th
                    key={hi}
                    className={`text-left py-3 px-4 font-semibold ${hi === 1 ? "text-accent-700 bg-accent-50" : "text-foreground-900"}`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.slice(1).map((row: string[], ri: number) => (
                <tr key={ri} className="border-b border-background-100">
                  {row.map((cell: string, ci: number) => (
                    <td key={ci} className={`py-3 px-4 ${ci === 1 ? "text-accent-700 bg-accent-50/50 font-medium" : "text-foreground-600"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (section.type === "items" && section.items) {
      return (
        <div className="space-y-4 mt-4">
          {section.items.map((item, ii) => (
            <div key={ii} className="bg-background-100 rounded-lg p-5">
              <h3 className="font-semibold text-foreground-900 mb-2">{item.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      );
    }

    return <p className="text-foreground-700 leading-relaxed mt-4">{section.content}</p>;
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | Superbuy Spreadsheet`}
        description={article.subtitle}
        keywords={seoKeywords}
        canonicalPath={slug}
        ogType="article"
        ogImage={article.image}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="px-6 md:px-12 py-4 bg-background-100">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </button>
            <span>/</span>
            <span className="text-foreground-800 font-medium">{article.title}</span>
          </nav>
        </div>

        <article className="px-6 md:px-12 py-10 md:py-14 max-w-4xl mx-auto">
          {/* Article Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">{article.tag}</span>
              <span className="text-xs text-foreground-500">Updated June 2026</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.subtitle}
            </p>
            <img
              src={article.image}
              alt={article.title}
              className="w-full rounded-lg mt-8"
            />
          </header>

          {/* CTA Banner */}
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-center mb-8">
            <p className="text-sm font-semibold text-accent-800 mb-3">
              Ready to start shopping? Visit our main website for the latest spreadsheet updates.
            </p>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Browse superbuy spreadsheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Intro */}
          <section className="mb-8">
            <p
              className="text-foreground-700 leading-relaxed text-base md:text-lg"
              dangerouslySetInnerHTML={{ __html: article.intro }}
            />
          </section>

          {/* Sections */}
          <div className="space-y-10">
            {article.sections.map((section, si) => (
              <section key={si}>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                  {section.heading}
                </h2>
                {renderSectionContent(section)}
              </section>
            ))}
          </div>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, fi) => (
                <details key={fi} className="bg-background-100 rounded-lg group">
                  <summary className="px-5 py-4 font-medium text-foreground-900 cursor-pointer list-none flex items-center justify-between">
                    {faq.q}
                    <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                    <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Internal Links */}
          <section className="mt-12 bg-background-100 rounded-lg p-6">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              Explore More Resources
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left">
                <i className="ri-home-4-line text-accent-500"></i>
                Back to Homepage
              </button>
              <button onClick={() => { navigate("/superbuy-spreadsheet-guide"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left">
                <i className="ri-book-2-line text-accent-500"></i>
                Superbuy Spreadsheet Guide
              </button>
              <button onClick={() => { navigate("/superbuy-shopping-guide"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer text-left">
                <i className="ri-shopping-bag-line text-accent-500"></i>
                Shopping Guide for Beginners
              </button>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-4 py-3 bg-background-50 rounded-lg text-sm text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-accent-500"></i>
                Visit Main Website
              </a>
            </div>
          </section>

          {/* Product Categories Quick Links */}
          <section className="mt-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
              Browse Product Categories
            </h2>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-2 bg-background-100 hover:bg-accent-100 text-sm text-foreground-700 hover:text-accent-800 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <div className="mt-12 text-center py-8 bg-primary-500 rounded-lg">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
              Start Shopping Smarter Today
            </h2>
            <p className="text-background-50/80 text-sm mb-5 max-w-md mx-auto">
              Access the complete superbuy spreadsheet with thousands of curated finds, trusted sellers, and real buyer reviews.
            </p>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 font-semibold text-sm rounded-full hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              superbuy spreadsheet
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}